import { FRONTEND_LOG_OPERATION_MESSAGE } from "../const/frontend-logs";
import { LOCAL_LOG_SECRET_KEY } from "../const/local-logs";
import type { FrontendLogSink } from "../types/frontend-logs";
import { getFrontendErrorFields } from "../utils/frontend-logs";
import { serializeLocalLog } from "../utils/local-logs";

type DebugLogLevel = "debug" | "verbose" | "deepDebug" | "error";
type DebugLogFields = Record<string, string | number | boolean | null | undefined>;

let sink: FrontendLogSink | undefined;

export const setDebugLogSink = (next: FrontendLogSink) => {
  sink = next;
};

const isDebugLogEnabled = (level: DebugLogLevel) => {
  if (level === "error") return true;
  const enabled = process.env.REACT_APP_DEBUG_LOGS;
  if (enabled === "false" || (process.env.NODE_ENV === "test" && enabled !== "true")) return false;
  if (level === "verbose") return ["verbose", "deepDebug"].includes(process.env.REACT_APP_DEBUG_LOG_LEVEL ?? "");
  if (level === "deepDebug") return process.env.REACT_APP_DEBUG_LOG_LEVEL === "deepDebug";
  return true;
};

const writeDebugLog = (level: DebugLogLevel, message: string, fields?: DebugLogFields) => {
  if (!isDebugLogEnabled(level)) return;

  const details = fields
    ? Object.entries(fields)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${LOCAL_LOG_SECRET_KEY.test(key) ? "[redacted]" : String(value)}`)
        .join(" ")
    : "";

  const text = serializeLocalLog([`${message}${details ? ` ${details}` : ""}`]);
  if (level === "error") console.error(text);
  else if (level === "debug") console.info(text);
  else console.debug(text);
  // Verbose render/lifecycle diagnostics remain local, even when remote collection is enabled.
  if (level === "debug" || level === "error") {
    try {
      sink?.(level, text);
    } catch {
      /* Diagnostics must never interrupt the application. */
    }
  }
};

export const debugLogger = {
  debug(message: string, fields?: DebugLogFields) {
    writeDebugLog("debug", message, fields);
  },
  verbose(message: string, fields?: DebugLogFields) {
    writeDebugLog("verbose", message, fields);
  },
  deepDebug(message: string, fields?: DebugLogFields) {
    writeDebugLog("deepDebug", message, fields);
  },
};

export const log = (message: string, fields?: DebugLogFields) => debugLogger.debug(message, fields);
export const logV = (message: string, fields?: DebugLogFields) => debugLogger.verbose(message, fields);
export const logUv = (message: string, fields?: DebugLogFields) => debugLogger.deepDebug(message, fields);
export const logE = (error: unknown, fields?: DebugLogFields) => {
  // Legacy callers pass native/Axios errors. Their message may embed response
  // content; relay only explicit operation labels, never arbitrary error text.
  const { operation, ...details } = fields ?? {};
  const message =
    typeof operation === "string" && FRONTEND_LOG_OPERATION_MESSAGE.test(operation)
      ? operation
      : error instanceof Error && FRONTEND_LOG_OPERATION_MESSAGE.test(error.message)
        ? error.message
        : "ERROR - exception";
  writeDebugLog("error", message, { ...getFrontendErrorFields(error), ...details });
};
